import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Modal from '../Modals/Modal';
import Text from '../Text/Text';
import Button from '../Button';
import useSafePush from 'src/hooks/useSafePush';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import BannerInfoBox from './SweepstakesBannerInfoBox';
import RuleRow from './RuleRow';
import useSweepstakesStore from 'src/stores/useSweepstakesStore';
import { twMerge } from 'tailwind-merge';
import { useCallback, useEffect, useState } from 'react';
import useDriftActions from 'src/hooks/useDriftActions';
import {
	DRIFT_COMPETITION_PUBKEY,
	INIT_COMPETITOR_BONUS_SCORE,
} from 'src/constants/sweepstakes';
import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import confetti from 'canvas-confetti';
import {
	Close,
	DrawAnnounce,
	DrawPrize,
	DrawTicket,
	DrawGift,
} from '@drift-labs/icons';
import DoubleBorderContainer from './DoubleBorderContainer';
import InfoMessage from '../TradeForm/InfoMessage';
import Countdown from '../Countdown';
import { useShallow } from 'zustand/react/shallow';

const AnnouncementPage = ({
	onClose,
	setPage,
	showUnableToJoinWarning,
}: {
	onClose: () => void;
	setPage: (page: DriftDrawAnnouncementPage) => void;
	showUnableToJoinWarning: boolean;
}) => {
	const connected = useWalletIsConnected();
	const safePush = useSafePush();
	const appActions = useDriftActions();
	const [
		setSweepstakesStore,
		isCompetitionLoaded,
		maxTotalPrizeAmount,
		nextDrawTs,
		userIsCompetitor,
		hasDisplayedConfettiInAnnouncementOnce,
		roundEndedDisplayString,
		isCompetitionResolving,
	] = useSweepstakesStore(
		useShallow((s) => [
			s.set,
			s.competition.subscribed,
			s.getMaxTotalPrize(),
			s.competition.accountData?.nextRoundExpiryTs?.toNumber(),
			s.isCompetitor(),
			s.ui.hasDisplayedConfettiInAnnouncementOnce,
			s.getRoundEndedDisplayString(),
			s.isCompetitionResolving(),
		])
	);
	const [isLoading, setIsLoading] = useState(false);

	const isCtaDisabled = isLoading || showUnableToJoinWarning;
	const prizeAmountNum = BigNum.from(
		maxTotalPrizeAmount,
		QUOTE_PRECISION_EXP
	).toNum();
	const isCompetitionLoading = !isCompetitionLoaded;

	// confetti effect
	useEffect(() => {
		if (hasDisplayedConfettiInAnnouncementOnce) return;
		confetti({
			colors: [
				'#e8a2a0',
				'#c187c5',
				'#b47ed2',
				'#aa77db',
				'#a070e5',
				'#9468f1',
				'#8d7cef',
				'#8690ee',
				'#7fa4ec',
				'#78b8eb',
				'#71cce9',
			],
			origin: {
				y: 0.5,
			},
			particleCount: 100,
			spread: 75,
			gravity: 0.5,
		});
		setSweepstakesStore((s) => {
			s.ui.hasDisplayedConfettiInAnnouncementOnce = true;
		});
	}, []);

	const handleConnectWallet = () => {
		setSweepstakesStore((s) => {
			s.ui.openAnnouncementModalOnConnect = true;
		});
		onClose();
		safePush('/onboarding/connect');
	};

	const handleCtaClick = async () => {
		if (!connected) {
			handleConnectWallet();
		} else {
			setIsLoading(true);
			try {
				const initSuccess = await appActions.initCompetitor(
					DRIFT_COMPETITION_PUBKEY
				);
				if (initSuccess) {
					setPage(DriftDrawAnnouncementPage.FreeTicketsClaim);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setIsLoading(false);
			}
		}
	};

	const notionalMillifiedFormatter = useCallback(
		(val: number) =>
			`$${BigNum.fromPrint(val.toString(), QUOTE_PRECISION_EXP).toMillified()}`,
		[]
	);

	const handleClaimDriftDrawTicket = () => {
		appActions.claimFreeTickets(DRIFT_COMPETITION_PUBKEY);
	};

	return (
		<>
			{/** Close button */}
			<div
				className="absolute cursor-pointer top-4 right-4 text-text-emphasis sm:hidden"
				onClick={onClose}
			>
				<Close size={24} />
			</div>

			{/** Banner */}
			<div
				className="h-[154px] w-full pt-8 pb-16 flex items-center justify-center flex-col"
				style={{
					backgroundImage: 'url(/assets/images/sweepstake-banner-bg.png)',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<img
					src="/assets/images/sweepstake-introducing-logo.svg"
					className="w-[339px]"
				/>
			</div>

			{/** Stats Container */}
			<div className="flex flex-col items-center w-full">
				<div className="relative -top-[50%]">
					<DoubleBorderContainer className="-translate-y-1/2">
						<div
							className={twMerge(
								'flex items-center justify-between gap-4',
								isCompetitionResolving && 'gap-3'
							)}
						>
							<BannerInfoBox
								Icon={DrawPrize}
								label="Total Prize"
								countUp={
									maxTotalPrizeAmount.gt(ZERO)
										? {
												end: prizeAmountNum,
												formattingFn: notionalMillifiedFormatter,
												className: twMerge('block w-[80px]'),
										  }
										: undefined
								}
								loading={isCompetitionLoading}
								value={maxTotalPrizeAmount.gt(ZERO) ? undefined : '$0.00'}
								skeletonClassName="w-[80px] h-[20px]"
							/>
							<div className="w-[1.5px] h-[32px] bg-container-border" />
							<BannerInfoBox
								Icon={DrawAnnounce}
								label="Next Draw"
								value={
									<Countdown
										endTs={nextDrawTs}
										minWidth={140}
										endedDisplay={roundEndedDisplayString}
										className={twMerge(
											isCompetitionResolving && 'animate-pulse'
										)}
									/>
								}
								loading={isCompetitionLoading}
								skeletonClassName="w-[68px] h-[20px]"
							/>
						</div>
					</DoubleBorderContainer>
				</div>
			</div>

			{/** Information & Rules */}
			<div
				className={twMerge(
					'flex flex-col gap-6 px-6 -mt-1 pb-4',
					userIsCompetitor && 'pb-6'
				)}
			>
				<Text.BODY3 className="text-text-default">
					Drift Draw is a weekly event you can enter for the chance to win the
					Grand Prize. Here&apos;s a quick summary of how it works:
				</Text.BODY3>

				<RuleRow
					header="Increasing your odds of winning"
					description={
						<span>
							Earn up to 10 tickets for every $1 in{' '}
							<span className="underline">taker volume</span> traded.
						</span>
					}
					Icon={DrawTicket}
				/>
				<RuleRow
					header="Weekly draws & winners"
					description="Every Monday at 2pm UTC, 1 of 3 potential prize pools will be randomly selected to be distributed amongst winners. 3 lucky users will be randomly selected for the top prize."
					Icon={DrawAnnounce}
				/>
				<RuleRow
					header="Consolation prizes"
					description="Didn’t snag the top prize? Up to 30 users will be rewarded with a special consolation prize"
					Icon={DrawGift}
				/>
				<RuleRow
					header="Prize pools can grow"
					description="If the Grand Prize wasn’t selected, the remainder is carried over to grow the prize pools the following week!"
					Icon={DrawPrize}
				/>

				{!connected || userIsCompetitor ? (
					<div className="inline-flex items-center justify-between w-full">
						<div>
							<Text.BODY3 className="text-text-default">
								For the complete parameters and terms,{' '}
								<a
									href="https://docs.drift.trade/drift-draw/overview"
									target="_blank"
									rel="noopener noreferrer"
									className="underline text-interactive-link"
									onClick={() => {
										window.open(
											'https://docs.drift.trade/drift-draw/overview',
											'_blank'
										);
									}}
								>
									click here
								</a>
								.
							</Text.BODY3>
						</div>
						{connected && userIsCompetitor && (
							<div>
								<Button.Secondary
									className="!text-[11px]"
									onClick={handleClaimDriftDrawTicket}
									size="SMALL"
								>
									Claim Ticket
								</Button.Secondary>
							</div>
						)}
					</div>
				) : (
					<span className="text-text-default">
						For full contest details, learn more{' '}
						<a
							className="underline cursor-pointer text-interactive-link"
							href="https://docs.drift.trade/drift-draw/overview"
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							here
						</a>
						. By continuing, you agree to the{' '}
						<a
							className="underline cursor-pointer text-interactive-link"
							href="https://docs.drift.trade/drift-draw/parameters"
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							Rules
						</a>{' '}
						and are eligible to use Drift pursuant to its{' '}
						<a
							className="underline cursor-pointer text-interactive-link"
							href="https://docs.drift.trade/legal/terms-of-use"
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							Terms
						</a>
						.
					</span>
				)}

				{showUnableToJoinWarning && (
					<InfoMessage
						type="warn"
						message="The previous round has ended and prizes are being distributed. Once this is complete, you’ll be able to join the next round of the Draw. Please check back in a few minutes"
					/>
				)}

				{!userIsCompetitor && (
					<div className="flex flex-col gap-1">
						<Button.Primary
							size="MEDIUM"
							className={twMerge(
								'w-full font-medium',
								!isCtaDisabled && `bg-brand-gradient text-container-bg`
							)}
							onClick={handleCtaClick}
							disabled={isCtaDisabled}
						>
							{connected ? 'Agree and Continue' : 'Connect Wallet'}
						</Button.Primary>
						<Button.Ghost
							size="MEDIUM"
							className="w-full text-xs hover:bg-transparent hover:opacity-60 text-text-interactive"
							onClick={onClose}
						>
							Dismiss
						</Button.Ghost>
					</div>
				)}
			</div>
		</>
	);
};

const FreeTicketsClaimPage = ({ onClose }: { onClose: () => void }) => {
	return (
		<div
			className="h-[437px] relative overflow-hidden"
			style={{
				backgroundImage: 'url(/assets/images/sweepstake-banner-bg.png)',
				backgroundSize: 'cover',
				backgroundPosition: '50% 10%',
				backgroundRepeat: 'no-repeat',
			}}
		>
			<div className="freeTicketsBackgroundShade" />

			<div
				className="absolute z-20 cursor-pointer top-4 right-4 text-text-emphasis"
				onClick={onClose}
			>
				<Close size={24} />
			</div>

			<div className="z-10 flex flex-col items-center justify-center p-6 pt-0">
				<div className="relative flex flex-col items-center justify-center w-full grow">
					<img
						src="/assets/icons/sweepstake-glow-ticket.svg"
						className="w-[300px] h-[300px]"
					/>
					<span className="z-20 uppercase font-bold font-['Chakra_Petch'] text-[16px] text-text-emphasis text-center tracking-[4.8px]">
						You&apos;ve received
					</span>

					{/** Decorative UI line */}
					<div className="relative flex justify-end w-full top-[6px]">
						<div className="h-[1px] w-[50%] bg-white right-0 -mr-6 absolute" />
					</div>

					<span className="z-20 text-[36px] sm:text-[46px] leading-[140%] tracking-[3.4px] font-['Retronoid'] text-text-emphasis text-center">
						{INIT_COMPETITOR_BONUS_SCORE} free ticket
						{INIT_COMPETITOR_BONUS_SCORE > 1 && 's'}
					</span>

					{/** Decorative UI lines */}
					<div className="relative flex w-full">
						<div className="h-[2.5px] w-[50%] bg-white left-10 absolute" />
					</div>
					<div className="relative flex w-full top-[6px]">
						<div className="h-[4px] w-[50%] bg-[#3FE4EA] left-0 -ml-6 rounded-r" />
					</div>

					<div className="absolute transform -translate-x-1/2 -translate-y-1/2 freeTicketsRadialGlow grow" />
				</div>
			</div>
		</div>
	);
};

enum DriftDrawAnnouncementPage {
	Announcement,
	FreeTicketsClaim,
}

export default function SweepstakesAnnouncementModal() {
	const connected = useWalletIsConnected();
	const setStore = useDriftStore((s) => s.set);
	const [page, setPage] = useState(DriftDrawAnnouncementPage.Announcement);
	const [isChangingPage, setIsChangingPage] = useState(false);

	const [userIsCompetitor, isCompetitorActive, isCompetitionExpired] =
		useSweepstakesStore(
			useShallow((s) => [
				s.isCompetitor(),
				s.isCompetitionActive(),
				s.isCompetitionExpired(),
			])
		);

	// new non-competitors are allowed to join competition when prizes are being distributed
	const showUnableToJoinWarning =
		!isCompetitorActive &&
		!isCompetitionExpired && // not active and not expired means the previous round has ended and prizes are being distributed currently
		!userIsCompetitor &&
		connected;

	const onClose = () => {
		setStore((s) => {
			s.modals.showSweepStakeModal = false;
		});
	};

	const animatePageChange = (newPage: DriftDrawAnnouncementPage) => {
		setIsChangingPage(true);
		setTimeout(() => {
			setPage(newPage);
			setIsChangingPage(false);
		}, 300);
	};

	return (
		<Modal
			onClose={onClose}
			id="sweepstakes-announcement-modal"
			className={twMerge('dark-theme')}
			gridContainerClassName={showUnableToJoinWarning && '!py-[2%]'}
			centered
		>
			<Modal.Body
				className={twMerge(
					'overflow-auto relative sm:min-w-[491px] sm:max-w-[491px] transition-[opacity] bg-container-bg max-h-[94vh]',
					isChangingPage ? 'opacity-0' : 'opacity-100'
				)}
			>
				{page === DriftDrawAnnouncementPage.Announcement ? (
					<AnnouncementPage
						onClose={onClose}
						setPage={animatePageChange}
						showUnableToJoinWarning={showUnableToJoinWarning}
					/>
				) : (
					<FreeTicketsClaimPage onClose={onClose} />
				)}
			</Modal.Body>
		</Modal>
	);
}
